import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { getApiEndpoint } from "../../config/config";
import DisplayMessage from "../shared/DisplayMessage/DisplayMessage";

function TrainingTabAllVideosPage() {
  const { getVerifiedJWT } = useAuth();
  const navigate = useNavigate();

  const [allVideos, setAllVideos] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [videoSearchQuery, setVideoSearchQuery] = useState("");
  const [videoFilterCourse, setVideoFilterCourse] = useState("all");
  const [sortBy, setSortBy] = useState("most-recent");
  const [viewMode, setViewMode] = useState("grid");

  useEffect(() => {
    const fetchAllVideosData = async () => {
      try {
        setLoading(true);
        setError(null);

        const jwt = await getVerifiedJWT();
        if (!jwt) {
          setAllVideos([]);
          setCourses([]);
          return;
        }

        const response = await fetch(
          getApiEndpoint("/v1/training/courses-videos"),
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${jwt}`,
            },
            cache: "no-cache",
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch videos");
        }

        const result = await response.json();
        const data = result?.data || {};

        setAllVideos(data.videos || []);
        setCourses(data.courses || []);
      } catch (err) {
        setError(err.message || "Failed to fetch videos");
        setAllVideos([]);
        setCourses([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAllVideosData();
  }, [getVerifiedJWT]);

  const filteredAllVideos = useMemo(() => {
    return (allVideos || [])
      .filter(video => {
        const matchesSearch = (video.video_title || "")
          .toLowerCase()
          .includes(videoSearchQuery.toLowerCase());

        const matchesCourse =
          videoFilterCourse === "all" ||
          video.course_id === Number(videoFilterCourse);

        return matchesSearch && matchesCourse;
      })
      .sort((a, b) => {
        switch (sortBy) {
          case "most-recent":
            return (
              new Date(b.created_at).getTime() -
              new Date(a.created_at).getTime()
            );
          case "oldest":
            return (
              new Date(a.created_at).getTime() -
              new Date(b.created_at).getTime()
            );
          case "title":
            return (a.video_title || "").localeCompare(b.video_title || "");
          default:
            return 0;
        }
      });
  }, [allVideos, videoFilterCourse, videoSearchQuery, sortBy]);

  const formatDuration = seconds => {
    const numericSeconds = Number(seconds);

    if (!Number.isFinite(numericSeconds) || numericSeconds < 0) {
      return "0:00";
    }

    const safeSeconds = Math.floor(numericSeconds);
    const minutes = Math.floor(safeSeconds / 60);
    const secs = safeSeconds % 60;
    return `${minutes}:${secs.toString().padStart(2, "0")}`;
  };

  if (loading) {
    return (
      <div className="training-page-no-courses-message">Loading videos…</div>
    );
  }

  if (error) {
    return (
      <DisplayMessage type="error" message={`Error loading videos: ${error}`} />
    );
  }

  return (
    <div className="training-page-all-videos-view">
      <div className="training-page-all-videos-header">
        <div>
          <h2 className="training-page-all-videos-title">All Videos</h2>
          <p className="training-page-all-videos-count">
            {filteredAllVideos.length} videos available
          </p>
        </div>
      </div>

      <div className="training-page-all-videos-controls">
        <div className="training-page-all-videos-search">
          <svg
            className="training-page-all-videos-search-icon"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="8"></circle>
            <path d="m21 21-4.35-4.35"></path>
          </svg>
          <input
            type="text"
            placeholder="Search videos..."
            value={videoSearchQuery}
            onChange={e => setVideoSearchQuery(e.target.value)}
            className="training-page-all-videos-search-input"
          />
        </div>

        <select
          className="training-page-all-videos-filter"
          value={videoFilterCourse}
          onChange={e => setVideoFilterCourse(e.target.value)}
        >
          <option value="all">All Courses</option>
          {courses.map(course => (
            <option key={course.id} value={course.id}>
              {course.course_title}
            </option>
          ))}
        </select>

        <select
          className="training-page-all-videos-filter"
          value={sortBy}
          onChange={e => setSortBy(e.target.value)}
        >
          <option value="most-recent">Most Recent</option>
          <option value="oldest">Oldest</option>
          <option value="title">Title</option>
        </select>

        <div className="training-page-all-videos-view-toggle">
          <button
            className={`training-page-view-toggle-btn ${
              viewMode === "grid" ? "active" : ""
            }`}
            onClick={() => setViewMode("grid")}
            aria-label="Grid view"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <rect x="3" y="3" width="7" height="7" rx="1"></rect>
              <rect x="14" y="3" width="7" height="7" rx="1"></rect>
              <rect x="3" y="14" width="7" height="7" rx="1"></rect>
              <rect x="14" y="14" width="7" height="7" rx="1"></rect>
            </svg>
          </button>
          <button
            className={`training-page-view-toggle-btn ${
              viewMode === "list" ? "active" : ""
            }`}
            onClick={() => setViewMode("list")}
            aria-label="List view"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <line x1="8" y1="6" x2="21" y2="6"></line>
              <line x1="8" y1="12" x2="21" y2="12"></line>
              <line x1="8" y1="18" x2="21" y2="18"></line>
              <line x1="3" y1="6" x2="3.01" y2="6"></line>
              <line x1="3" y1="12" x2="3.01" y2="12"></line>
              <line x1="3" y1="18" x2="3.01" y2="18"></line>
            </svg>
          </button>
        </div>
      </div>

      <div className={`training-page-all-videos-grid ${viewMode}`}>
        {filteredAllVideos.map(video => (
          <div
            key={`${video.course_id}-${video.id}`}
            className="training-page-video-card"
            onClick={() =>
              navigate(`/course/${video.course_id}`, {
                state: {
                  videoId: video.id,
                  fromTraining: true,
                  trainingTab: "All Videos",
                },
              })
            }
          >
            <div className="training-page-video-card-thumbnail">
              <img
                src={video.video_thumbnail || video.course_thumbnail}
                alt={video.video_title}
                className="training-page-video-card-image"
                loading="lazy"
              />

              {(() => {
                const twoWeeksAgo = new Date();
                twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
                const isNew = new Date(video.created_at) > twoWeeksAgo;

                if (isNew) {
                  return (
                    <div className="training-page-video-card-badge new">
                      New
                    </div>
                  );
                }

                if (video.video_completed) {
                  return (
                    <div className="training-page-video-card-badge watched">
                      Watched
                    </div>
                  );
                }

                return (
                  <div className="training-page-video-card-badge unwatched">
                    Unwatched
                  </div>
                );
              })()}

              {video.duration && (
                <div className="training-page-video-duration">
                  {formatDuration(video.duration)}
                </div>
              )}
            </div>

            <div className="training-page-video-card-content">
              <h3 className="training-page-video-card-title">
                {video.video_title}
              </h3>
              <p className="training-page-video-card-course">
                {video.course_title}
              </p>
            </div>
          </div>
        ))}
      </div>

      {filteredAllVideos.length === 0 && (
        <div className="training-page-no-videos-message">
          No videos found matching your criteria.
        </div>
      )}
    </div>
  );
}

export default TrainingTabAllVideosPage;
